import { useEffect, useState } from 'react';
import { WifiOff, Wifi, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useOfflineQueue } from '@/hooks/useOfflineQueue';
import { Button } from '@/components/ui/button';
import { OfflineQueueIndicator } from './OfflineQueueIndicator';

export function OfflineIndicator() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showIndicator, setShowIndicator] = useState(false);
  const { pendingCount, syncQueue, isSyncing } = useOfflineQueue();

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setShowIndicator(true);
      setTimeout(() => setShowIndicator(false), 3000);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowIndicator(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return (
    <>
      {/* Connection status banner */}
      {showIndicator && (
        <div
          className={cn(
            "fixed bottom-4 left-1/2 -translate-x-1/2 z-50",
            "px-4 py-2 rounded-full shadow-lg",
            "flex items-center gap-2",
            "transition-all duration-300 ease-in-out",
            "text-sm font-medium",
            isOnline
              ? "bg-green-500 text-white"
              : "bg-destructive text-destructive-foreground"
          )}
          role="status"
          aria-live="polite"
        >
          {isOnline ? (
            <>
              <Wifi className="h-4 w-4" />
              <span>Back online</span>
              {pendingCount > 0 && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={syncQueue}
                  disabled={isSyncing}
                  className="ml-2 h-6"
                >
                  <RefreshCw className={cn("h-3 w-3 mr-1", isSyncing && "animate-spin")} />
                  Sync {pendingCount}
                </Button>
              )}
            </>
          ) : (
            <>
              <WifiOff className="h-4 w-4" />
              <span>You're offline</span>
              {pendingCount > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-background/20 rounded-full text-xs">
                  {pendingCount} queued
                </span>
              )}
            </>
          )}
        </div>
      )}

      {/* Persistent queue indicator (top right) */}
      <div className="fixed top-4 right-4 z-50">
        <OfflineQueueIndicator />
      </div>
    </>
  );
}
